<template>
  <div class="collection-hero critical-component-clear">
    <div class="container">
      <breadcrumbs
        :title="title"
        state="ready"
      />

      <div class="collection-hero__grid grid">
        <div class="collection-hero__text-wrapper col xs-span l5">
          <h1
            :id="liquid.sectionId"
            class="collection-hero__title text-heading-xl text-heading-2xl-desktop"
            v-text="title"
          />

          <div class="collection-hero__description-wrapper">
            <div
              ref="description"
              class="collection-hero__description text-body-s-regular"
              :class="{
                [$class('collapsed')]: !readMore.enabled
              }"
              v-html="formattedDescription"
            />

            <button
              v-show="readMore.overflow"
              type="button"
              class="collection-hero__read-more"
              aria-hidden="true"
              @click="readMore.enabled = !readMore.enabled"
            >
              <span
                class="text-body-s-underline"
                v-text="readMoreLabel"
              />
            </button>
          </div>
        </div>

        <div class="collection-hero__content-wrapper col xs-span l7-12">
          <responsive-image
            v-if="image"
            class="collection-hero__featured-image"
            :ratio="2.5"
            :image="image"
            :lazyload="false"
            :loaded-animation="false"
          />

          <div
            v-else-if="collectionQuickLinks.collection"
            class="collection-quick-links"
            :class="
              collectionQuickLinks.collection?.length <= 3 ?
                'collection-quick-links--items-fluid' : 'collection-quick-links--items-fixed'
            "
          >
            <content-carousel
              :cards="collectionQuickLinks.collection"
              card-type="collection-quick-links"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Breadcrumbs from '~async/breadcrumbs/breadcrumbs.vue'
import ContentCarousel from '~async/content-carousel/content-carousel'
import ResponsiveImage from '~global/images/responsive-image.vue'

/**
 * Vue: Collection hero (collection-hero)
 * -----------------------------------------------------------------------------
 * A hero banner for the collection page.
 * - Component settings: section / dynamic / async / load
 *
 * @param {String} title - Title text.
 * @param {String} description - Description text.
 * @param {Object} image - Formatted image object.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
export default {
  name: 'CollectionHero',

  components: {
    ResponsiveImage,
    ContentCarousel,
    Breadcrumbs,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    image: {
      type: [Boolean, Object],
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    collectionQuickLinks: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      readMore: {
        overflow: true,
        enabled: false,
      },
    }
  },

  computed: {

    ...mapState({
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Format the description
     * - Remove extra elements, remove text align center and update classes.
     * @returns {String}
     */
    formattedDescription() {
      return this.description
        .replaceAll('<p><img', '<img')
        .replaceAll('alt></p>', 'alt>')
        .replaceAll('"></p>', '">')
        .replaceAll('text-align: center;', '')
        .replaceAll('<p', '<p class="text-body-s-regular"')
        .replaceAll('<a ', '<a class="text-body-s-underline"')
    },

    /**
     * Compute text for read more button text
     * @returns {String}
     */
    readMoreLabel() {
      return this.readMore.enabled
        ? this.$string('general.read_more.read_less')
        : this.$string('general.read_more.read_more')
    },
  },

  watch: {

    /**
     * Watch the screen width to re-check the description overflow.
     */
    screenWidth() {
      this.checkDescriptionOverflow()
    },
  },

  mounted() {
    this.checkDescriptionOverflow()
  },

  methods: {

    /**
     * Check if the description text is overflowing.
     * - Checks if the description text is larger than 2 lines tall or not.
     * - If it is not larger than 2 lines tall, then all the text is showing and
     *   we can update out read more state to hide the Read More/Read Less
     *   button.
     */
    checkDescriptionOverflow() {
      const element = this.$refs.description

      if (!element) {
        return
      }

      const collapsedLinesToShow = 2
      const { lineHeight } = getComputedStyle(element)
      const lineHeightNumber = parseInt(lineHeight)

      const totalCollapsedHeight = lineHeightNumber * collapsedLinesToShow

      const isDescriptionOverflow = element.scrollHeight > totalCollapsedHeight

      this.readMore.overflow = isDescriptionOverflow
    },
  },
}
</script>

<style lang="scss">
@import './collection-hero';
@import './collection-quick-links';
</style>
